import React from 'react';
import { View, Text, StyleSheet, ScrollView, useWindowDimensions } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { FONTS } from '../../constants/config';

import { tokens } from '@design';
// ============================================================
// Types
// ============================================================

interface ResponsiveChartContainerProps {
  children: React.ReactNode;
  title?: string;
  minHeight?: number;
  maxWidth?: number;
  showCard?: boolean;
  horizontalScroll?: boolean;
}

interface EmptyChartStateProps {
  height?: number;
  title?: string;
  message?: string;
  icon?: 'chart' | 'trend' | 'pie';
}

// ============================================================
// Chart Icons
// ============================================================

const ChartLineIcon = ({ size = 48, color = tokens.colors.raw.gray["400"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 3V21H21" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M7 16L12 11L16 15L21 10" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PieChartIcon = ({ size = 48, color = tokens.colors.raw.gray["400"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M21.21 15.89C20.5738 17.3945 19.5788 18.7202 18.3119 19.7513C17.045 20.7824 15.5448 21.4874 13.9424 21.8048C12.3401 22.1221 10.6845 22.0421 9.12015 21.5718C7.55585 21.1015 6.1306 20.2551 4.96903 19.1066C3.80746 17.958 2.94542 16.5426 2.45799 14.9839C1.97056 13.4251 1.87217 11.7707 2.17134 10.1653C2.47051 8.55991 3.15825 7.05256 4.17467 5.77448C5.1911 4.4964 6.50394 3.48637 8.00001 2.83002" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M22 12C22 10.6868 21.7413 9.38642 21.2388 8.17317C20.7362 6.95991 19.9997 5.85752 19.0711 4.92893C18.1425 4.00035 17.0401 3.26375 15.8268 2.7612C14.6136 2.25866 13.3132 2 12 2V12H22Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrendUpIcon = ({ size = 48, color = tokens.colors.raw.gray["400"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M23 6L13.5 15.5L8.5 10.5L1 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17 6H23V12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// ResponsiveChartContainer Component
// ============================================================

export const ResponsiveChartContainer = ({
  children,
  title,
  minHeight = 250,
  maxWidth = 600,
  showCard = true,
  horizontalScroll = true,
}: ResponsiveChartContainerProps) => {
  const { colors, isDark } = useTheme();
  const { width: screenWidth } = useWindowDimensions();

  const chartWidth = Math.min(screenWidth - 48, maxWidth);

  const containerStyle = showCard
    ? [
        styles.cardContainer,
        {
          backgroundColor: colors.card,
          borderColor: colors.cardBorder,
        },
      ]
    : styles.plainContainer;

  const content = (
    <View style={[styles.chartContent, { minHeight, minWidth: chartWidth }]}>
      {children}
    </View>
  );

  return (
    <View style={containerStyle}>
      {title && (
        <Text style={[styles.title, { color: colors.textPrimary }]}>{title}</Text>
      )}

      {horizontalScroll ? (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={[styles.scrollContent, { minWidth: chartWidth }]}
        >
          {content}
        </ScrollView>
      ) : (
        content
      )}
    </View>
  );
};

// ============================================================
// EmptyChartState Component
// ============================================================

export const EmptyChartState = ({
  height = 200,
  title,
  message,
  icon = 'chart',
}: EmptyChartStateProps) => {
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();

  // Use translations as defaults
  const displayTitle = title || t('charts.noData');
  const displayMessage = message || t('charts.noDataForChart');

  const IconComponent = {
    chart: ChartLineIcon,
    trend: TrendUpIcon,
    pie: PieChartIcon,
  }[icon];

  return (
    <View
      style={[
        styles.emptyState,
        {
          height,
          backgroundColor: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["50"],
        },
      ]}
    >
      <IconComponent size={48} color={colors.textMuted} />
      <Text style={[styles.emptyTitle, { color: colors.textPrimary }]}>{displayTitle}</Text>
      <Text style={[styles.emptyText, { color: colors.textMuted }]}>{displayMessage}</Text>
    </View>
  );
};

// ============================================================
// Styles
// ============================================================

const getStyles = (isRTL: boolean) => StyleSheet.create({
  cardContainer: {
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    padding: tokens.spacing.absolute["4"],
    marginVertical: tokens.spacing.absolute["2"],
  },
  plainContainer: {
    marginVertical: tokens.spacing.absolute["2"],
  },
  title: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
  },
  scrollContent: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  chartContent: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    borderRadius: tokens.radii.absolute["radius-12"],
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
    paddingHorizontal: tokens.spacing.absolute["6"],
  },
  emptyTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    textAlign: 'center',
  },
  emptyText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    textAlign: 'center',
    lineHeight: 20,
  },
});

export default ResponsiveChartContainer;

const styles = getStyles(true);
